import React from 'react';
import * as THREE from 'three';

interface TestTubeHolderProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Length of the wooden arms (handle + jaws). */
  armLength?: number;
}

/**
 * Wooden test-tube holder — the standard "wooden tongs" used to grip a
 * boiling/test tube while heating it over a Bunsen flame. Two slender
 * wooden arms hinged at one end with a metal-gripped jaw ring at the
 * other end to hold the tube body. The tube itself is positioned by the
 * caller (the holder just sits beneath/around it visually).
 */
export function TestTubeHolder({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  armLength = 0.18,
}: TestTubeHolderProps) {
  const armRadius = 0.0045;
  const jawInnerRadius = 0.032; // matches BoilingTube radius (0.055) loosely

  const Arm = ({ sign }: { sign: 1 | -1 }) => (
    <group position={[sign * 0.012, 0, 0]} rotation={[0, 0, sign * 0.06]}>
      <mesh position={[0, -armLength / 2, 0]} castShadow>
        <cylinderGeometry args={[armRadius, armRadius * 0.9, armLength, 12]} />
        <meshStandardMaterial color="#8a5a30" roughness={0.85} />
      </mesh>
      {/* Subtle grain stripe */}
      <mesh position={[0, -armLength / 2, armRadius + 0.0005]}>
        <boxGeometry args={[0.0015, armLength * 0.88, 0.0008]} />
        <meshStandardMaterial color="#5d3d1f" roughness={1} />
      </mesh>
    </group>
  );

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Wooden handles */}
      <Arm sign={1} />
      <Arm sign={-1} />

      {/* Hinge rivet at the back */}
      <mesh position={[0, 0, 0]} castShadow>
        <sphereGeometry args={[armRadius * 1.8, 12, 12]} />
        <meshStandardMaterial color="#4a3220" metalness={0.5} roughness={0.55} />
      </mesh>

      {/* Metal grip ring at the front (the jaws that hold the tube) */}
      <mesh position={[0, -armLength + 0.012, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[jawInnerRadius, 0.005, 10, 28]} />
        <meshStandardMaterial color="#9aa0a8" metalness={0.65} roughness={0.4} />
      </mesh>
      {/* Inner padding strip on the grip */}
      <mesh position={[0, -armLength + 0.012, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[jawInnerRadius - 0.002, 0.0025, 8, 28]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.95} />
      </mesh>
      {/* Connector struts from arms to grip ring */}
      {[1, -1].map((sign) => (
        <mesh key={sign} position={[sign * 0.014, -armLength + 0.006, 0]} rotation={[0, 0, sign * 0.12]}>
          <boxGeometry args={[0.005, 0.018, 0.005]} />
          <meshStandardMaterial color="#7a7a82" metalness={0.6} roughness={0.45} />
        </mesh>
      ))}
    </group>
  );
}

export default TestTubeHolder;
